import {List, ListItem, Typography} from '@mui/material';
import React from 'react';
import {ActionType} from '../../components/AutoForm/AutoFieldNew';
import {hasSpecificAnswer} from '../../components/Wizard/operators';
import {WizardField} from '../../components/Wizard/QuestionWrapper';
import {ContactAttribution} from '../PrecursorSummaryPage/headerFields';
import {longTermVul, shortTermVul} from './vulnerabilities';
import {camelCaseToCapitalisedString} from '../../utils/camelCaseToCapitalisedString';
import {postcursorFieldsType} from './postcursorFields';
import {AccountDetailsFragment} from '../../schema-types/AccountDetailsFragment';

export const vulnerabilityFields = (
  account: AccountDetailsFragment
): WizardField<postcursorFieldsType>[] => {
  const contacts = [];
  if (account!.primaryContact) {
    contacts.push(account!.primaryContact!);
  }
  if (account!.secondaryContact) {
    contacts.push(account!.secondaryContact!);
  }
  const precursorFields: WizardField<postcursorFieldsType>[][] = contacts.map((c) => {
    const contactId = c.id as string;
    return [
      {
        id: `isVulnerable.${contactId}` as any,
        title: `Does ${c.simplifiedName} have any long term vulnerabilities`,
        questionComponent: (
          <>
            <Typography variant="subtitle1">
              Does the client suffer from any{' '}
              <u>
                <strong>permanent disability</strong>
              </u>{' '}
              or do you have any other reason to believe the client is facing any{' '}
              <u>
                <strong>long-term</strong>
              </u>{' '}
              vulnerability?
            </Typography>
          </>
        ),
        label: '',
        enabled: (values) => {
          if (values.reviewContact === ContactAttribution.both) {
            return true;
          }
          if (values.reviewContact !== contactId) {
            return false;
          }
          return true;
        },
        // eslint-disable-next-line no-nested-ternary
        initialValue: undefined,
        type: ActionType.buttonGroup,
        requireSubmit: true,
        options: [
          {
            label: 'Yes',
            value: true,
          },
          {
            label: 'No',
            value: false,
          },
        ],
      },
      {
        id: `longTermVulnerabilityTypes.${contactId}` as any,
        title: `${c.simplifiedName}'s long term vulnerability`,
        questionComponent: (
          <>
            <Typography variant="subtitle1">
              Which of the following long-term vulnerabilities apply to {c.simplifiedName}
              ?
            </Typography>
          </>
        ),
        label: '',
        enabled: (values) => {
          if (
            values.reviewContact !== ContactAttribution.both &&
            values.reviewContact !== contactId
          ) {
            return false;
          }
          return hasSpecificAnswer<any>(`isVulnerable.${contactId}`, true)(values);
        },
        // eslint-disable-next-line no-nested-ternary
        initialValue: undefined,
        type: ActionType.checkboxGroup,
        requireSubmit: true,
        options: longTermVul.map((x) => ({
          value: x,
          label: camelCaseToCapitalisedString(x),
        })),
      },
      {
        id: `isEventVulnerable.${contactId}` as any,
        title: `Does ${c.simplifiedName} have any circumstantial vulnerabilities`,
        requireSubmit: true,
        questionComponent: (
          <>
            <Typography variant="h3">
              In the last 12 months has {c.simplifiedName}:
            </Typography>
            <List>
              <ListItem>Been widowed, divorced, or separated?</ListItem>
              <ListItem>Suffered serious illness or accident?</ListItem>
              <ListItem>
                Suffered a significant change in circumstances i.e. loss of job reduction
                in income?
              </ListItem>
              <ListItem>Suffered from Post Traumatic Stress Disorder?</ListItem>
            </List>
          </>
        ),
        label: '',
        enabled: (values) => {
          if (
            values.reviewContact !== ContactAttribution.both &&
            values.reviewContact !== contactId
          ) {
            return false;
          }

          return true;
        },
        initialValue: undefined,
        type: ActionType.buttonGroup,
        options: [
          {
            label: 'Yes',
            value: true,
          },
          {
            label: 'No',
            value: false,
          },
        ],
      },
      {
        id: `eventVulnerabilityTypes.${contactId}` as any,
        title: `${c.simplifiedName}'s circumstantial vulnerability`,
        question: `Which of the following circumstance changes apply to ${c.simplifiedName}?`,
        label: '',
        enabled: (values) => {
          if (
            values.reviewContact !== ContactAttribution.both &&
            values.reviewContact !== contactId
          ) {
            // if its not set as both contacts and the contactId doesnt match the single contact used in the review, show vulnerability
            return false;
          }
          return hasSpecificAnswer<any>(`isEventVulnerable.${contactId}`, true)(values);
        },
        // eslint-disable-next-line no-nested-ternary
        initialValue: undefined,
        type: ActionType.checkboxGroup,
        requireSubmit: true,
        options: shortTermVul.map((x) => ({
          value: x,
          label: camelCaseToCapitalisedString(x),
        })),
      },
      {
        id: `specialCareReasons.${contactId}` as any,
        title: `${c.simplifiedName}'s recent circumstance changes`,
        question: `What were the circumstance changes for ${c.simplifiedName}?`,
        label: 'Enter circumstance changes here',
        enabled: (values) => {
          if (
            values.reviewContact !== ContactAttribution.both &&
            values.reviewContact !== contactId
          ) {
            return false;
          }
          return hasSpecificAnswer<any>(`isEventVulnerable.${contactId}`, true)(values);
        },
        initialValue: '',
        type: ActionType.text,
        multiline: true,
        minRows: 3,
        requireSubmit: true,
        fullWidth: true,
        charLimit: 255,
        showCounter: true,
      },
    ];
  }) || [[]];
  return precursorFields.flat(1);
};
